/**
 * The arithmetic behind a partial QC verdict — and behind correcting one.
 *
 * THE BOX HOLDS THE TOTAL, NOT THE INCREMENT. Somebody standing over a pallet counts what
 * failed; somebody correcting last week's count knows what the true total should be. Both
 * edit one number — the batch's rejected total — and the system moves the difference. That
 * is the record-absolute / movement-incremental rule: the batch states the truth outright,
 * the ledger gains only the delta.
 *
 * TWO CEILINGS, CHECKED FOR DIFFERENT REASONS. The total can never exceed what the batch
 * made — more than that cannot have failed. And the ADDITIONAL write-off (the positive
 * delta) can never exceed what is still at the site: units already dispatched are the
 * brand's problem now, not something this screen can write off. The server's no-negative
 * guard refuses the latter too; failing in the browser lets the message name the real
 * reason instead of surfacing a stock error.
 */
export interface QcSplit {
    /** The rejected total being declared: what the box says, resolved against the seed. */
    total: number;
    /** What the verdict says passed: everything not rejected. */
    accepted: number;
    /** The change against the stored total: positive writes off, negative returns stock. */
    delta: number;
    /** Whether any of the batch is rejected at all under this declaration. */
    isPartial: boolean;
    /** Whether the total differs from what is already stored — i.e. this is a correction. */
    hasChange: boolean;
    /** The most the total can be: made, or what dispatch has left reachable. */
    maxRejectable: number;
    /** Set when the entry cannot be submitted, and says why in words a person can act on. */
    error: string | null;
}

export function resolveQcSplit(args: {
    /** What came into existence in this batch. */
    made: number;
    /** What the balance still holds at this site. */
    remaining: number;
    /** The rejected total already on the record. Zero when the batch was never split. */
    previousRejected?: number;
    /** What the person entering says the rejected total is. Blank means unchanged. */
    rejected: number | '';
}): QcSplit {
    const previous = args.previousRejected ?? 0;
    // Blank is "unchanged", not "zero": somebody clearing the box to retype should see
    // neither an error nor a promise that the whole prior rejection is being returned.
    const total = args.rejected === '' ? previous : args.rejected;
    const delta = total - previous;
    const maxRejectable = Math.min(args.made, previous + args.remaining);
    const base = {
        total,
        accepted: args.made - total,
        delta,
        isPartial: total > 0,
        hasChange: delta !== 0,
        maxRejectable,
    };

    if (!Number.isFinite(total) || total < 0) {
        return { ...base, error: 'A rejected quantity cannot be negative.' };
    }

    if (total > maxRejectable) {
        // The two ceilings fail for different reasons and a reader needs to know which.
        return {
            ...base,
            error:
                maxRejectable < args.made
                    ? `Only ${args.remaining} of this batch is still at the site — the rest has already gone out and cannot be written off here.`
                    : `This batch is ${args.made}. More than that cannot have failed.`,
        };
    }

    return { ...base, error: null };
}
